'use client';

import { useDeviceType } from '@frontastic/catwalk/js/helper/hooks/useDeviceType';
import React, { Fragment, useEffect } from 'react';
import { TasticWrapper } from '../../../frontastic/lib/component';
import { Cell as LayoutElement, DataSources, PageFolder } from '../../../frontastic/lib/types';
import { PageResponse } from '@commercetools/frontend-sdk/lib/types/api/page/PageResponse';
import { PagePreviewResponse } from '@commercetools/frontend-sdk/lib/types/api/page/PagePreviewResponse';
import { TasticRegistry } from '../../../frontastic/tastics/types';
import PrimaryLoader from '@srcDS/components/atoms/primaryLoader/PrimaryLoader';
import { usePageLoading } from '@frontastic/catwalk/js/helper/hooks/usePageLoading';
import deepFreeze from "../../../helpers/utils/deepFreeze";
import { store } from '../../redux/store';

const isPreviewDataResponse = (
  pageDataResponse: PageResponse | PagePreviewResponse,
): pageDataResponse is PagePreviewResponse => {
  return (pageDataResponse as PagePreviewResponse).previewId !== undefined;
};

const getPreviewId = (pageDataResponse: PageResponse | PagePreviewResponse) => {
  if (isPreviewDataResponse(pageDataResponse)) {
    return pageDataResponse.previewId;
  }

  return undefined;
};

const LegacyFrontasticRenderer = ({
  data,
  tastics = {},
  currentHighlight,
  isDevelopment,
}: {
  data: PageResponse | PagePreviewResponse;
  tastics: TasticRegistry;
  currentHighlight?: string;
  isDevelopment?: boolean;
}) => {
  const { isLoading, hideContent, dispatchPageLoadingDone, dispatchPageLoadingStart } = usePageLoading();

  const handleNavEvent = () => {
    if (store.getState().pageLoading?.suppressEventListeners === true) {
      return;
    }

    dispatchPageLoadingStart(false);
  };

  useEffect(() => {
    window.addEventListener('beforeunload', handleNavEvent);
    window.addEventListener('popstate', handleNavEvent);

    return () => {
      window.removeEventListener('beforeunload', handleNavEvent);
      window.removeEventListener('popstate', handleNavEvent);
    };
  }, []);

  useEffect(() => {
    dispatchPageLoadingDone();

    store.dispatch({
      type: 'Frontend.Node.initialize',
      data: {
        node: { ...data.pageFolder, nodeId: data.pageFolder.pageFolderId },
        page: data.page,
        data: data.data,
        route: {
          parameters: {},
        },
      },
    });
  }, [data]);

  if (isDevelopment === true) {
    data = deepFreeze(data);
  }

  // @ts-ignore The types are incorrect
  if (data?.page?.sections?.kit) {
    return (
      <main className="c-page-body">
        <div className="c-page-wrapper o-wrapper">
          <Region
            tastics={tastics}
            // @ts-ignore The types are incorrect
            layoutElements={data?.page?.sections?.kit?.layoutElements}
            pageFolder={data.pageFolder}
            dataSources={data.data.dataSources}
            previewId={getPreviewId(data)}
            currentHighlight={currentHighlight}
          />
        </div>
      </main>
    );
  }

  return (
    <Fragment>
      <header className="c-page-head o-wrapper">
        <Region
          tastics={tastics}
          // @ts-ignore The types are incorrect
          layoutElements={data?.page?.sections?.head?.layoutElements}
          pageFolder={data.pageFolder}
          dataSources={data.data.dataSources}
          previewId={getPreviewId(data)}
          currentHighlight={currentHighlight}
        />
      </header>
      {isLoading && hideContent ? (
        <PrimaryLoader mode={'global'} borderWidth={undefined} />
      ) : (
        <>
          {isLoading && !hideContent && <PrimaryLoader mode={'global'} borderWidth={undefined} />}
          <main className="c-page-body">
            <div className="c-page-wrapper o-wrapper">
              <Region
                tastics={tastics}
                // @ts-ignore The types are incorrect
                layoutElements={data?.page?.sections?.main?.layoutElements}
                pageFolder={data.pageFolder}
                dataSources={data.data.dataSources}
                previewId={getPreviewId(data)}
                currentHighlight={currentHighlight}
              />
            </div>
          </main>
          <footer className="c-page-foot o-wrapper">
            <Region
              tastics={tastics}
              // @ts-ignore The types are incorrect
              layoutElements={data?.page?.sections?.footer?.layoutElements}
              pageFolder={data.pageFolder}
              dataSources={data.data.dataSources}
              previewId={getPreviewId(data)}
              currentHighlight={currentHighlight}
            />
          </footer>
        </>
      )}
    </Fragment>
  );
};

const createRenderTree = (layoutElements: LayoutElement[], currentDeviceType: string) => {
  const renderTree = [[]];
  let row = 0;
  let colsPerRow = 0;

  for (let i = 0; i < layoutElements.length; i++) {
    if (!renderTree[row]) {
      renderTree[row] = [];
    }

    if (layoutElements[i].configuration[currentDeviceType]) {
      renderTree[row].push(layoutElements[i]);
    }

    colsPerRow += layoutElements[i].configuration.size || 12;

    if (colsPerRow >= 12) {
      colsPerRow = 0;
      row++;
    }
  }

  return renderTree;
};

const Row = ({ children }: React.PropsWithChildren<object>) => {
  return (
    <Cell size={12}>
      <Grid>{children}</Grid>
    </Cell>
  );
};

const Grid = ({ children }: React.PropsWithChildren<object>) => {
  return <div className={`o-grid`}>{children}</div>;
};

const Cell = ({
  size = 12,
  prefix = 'o-cell',
  highlight = false,
  children,
}: React.PropsWithChildren<{
  size?: 1 | 2 | 3 | 4 | 6 | 8 | 12;
  prefix?: string;
  highlight?: boolean;
}>) => {
  return (
    <div
      className={`${prefix} ${prefix}--${size}`}
      style={{
        outline: highlight ? '2px dashed #d73964' : null,
      }}
    >
      {children}
    </div>
  );
};

const Region = ({
  tastics = {},
  currentHighlight,
  layoutElements,
  pageFolder,
  dataSources,
  previewId,
}: {
  tastics: TasticRegistry;
  currentHighlight?: string;
  layoutElements: LayoutElement[];
  pageFolder: PageFolder;
  dataSources: DataSources;
  previewId?: string;
}) => {
  const deviceType = useDeviceType();

  const renderTree = createRenderTree(layoutElements, deviceType);

  return (
    <div className={`o-grid o-region`}>
      {renderTree
        .filter((group) => group.length > 0)
        .map((group, i) => {
          return (
            <Row key={`row_${i}`}>
              {group.map((cell) => {
                return (
                  <Cell
                    key={cell.layoutElementId}
                    size={cell.configuration.size || 12}
                    highlight={currentHighlight === cell.layoutElementId}
                  >
                    {cell.tastics.map((t) => (
                      <TasticWrapper
                        tastics={tastics}
                        key={t.tasticId}
                        data={t}
                        dataSources={dataSources}
                        pageFolder={pageFolder}
                        highlight={currentHighlight === t.tasticId}
                        previewId={previewId}
                      />
                    ))}
                  </Cell>
                );
              })}
            </Row>
          );
        })}
    </div>
  );
};

export default LegacyFrontasticRenderer;
